import { Dispatch, SetStateAction, useMemo } from "react";

import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../../constants";
import useActionShipment from "../../../hooks/useActionShipment";
import { DocumentReferenceOptions, DocumentReferenceShipment } from "../../../models/document";
import { ShipmentDocument } from "../../../models/shipment.document";
import { downloadAndGenerateCsv } from "../../../util/downloadAndGenerateCsv";
import { handleGeneratePdf } from "../../../util/generateReport";
import { NotificationToast } from "../../Generic/Toast/NotificationToast";
import useActionBooking from "../actions";

interface UseBookingFilesProps {
  bookingId?: number;
  documents?: ShipmentDocument[];
  viewOnly?: boolean;
  onChangeDocuments?: (documents: ShipmentDocument[]) => void;
  setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>>;
  hasBookingId: boolean;
}
export const useBookingFiles = ({
  bookingId,
  documents,
  viewOnly,
  hasBookingId = false,
  onChangeDocuments,
  setHasUnsavedChanges,
}: UseBookingFilesProps) => {
  const {
    useDownloadCartonLogs,
    useDownloadInvoicePackingDocument,
    useDownloadCiplCSV,
    useDownloadAllDocumentsZip,
    useUpdateShipmentDocuments,
  } = useActionBooking();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const { useUploadDocument } = useActionShipment();
  const { mutate: downloadCartonLogs, isLoading: loadingCartonLogs } = useDownloadCartonLogs();
  const { mutate: downloadInvoicePackingDocument, isLoading: loadingBookingDocuments } =
    useDownloadInvoicePackingDocument();
  const { mutate: downloadCiplCSV, isLoading: loadingCiplCsv } = useDownloadCiplCSV();
  const { mutate: downloadAllDocumentsZip, isLoading: loadingAllDocumentsZip } =
    useDownloadAllDocumentsZip();
  const { mutate: updateShipmentDocuments, isLoading: loadingUpdateShipmentDocuments } =
    useUpdateShipmentDocuments(
      viewOnly
        ? () => {
            setHasUnsavedChanges?.(false);
            NotificationToast.success(translate("documentsUploadedSuccessfully"));
            history.push(PRIVATE_ROUTES.BOOKINGS);
          }
        : undefined
    );

  const onDownloadAllDocuments = () => {
    if (!documents || !bookingId) return;

    downloadAllDocumentsZip({ shipmentId: bookingId, documents, hasBookingId });
  };
  const handleDownloadCartonLogs = (bookingId: number, onFinally?: () => void) => {
    downloadCartonLogs(bookingId, {
      onSuccess: (response) => {
        if (onFinally) onFinally();
        if (response && response.data) {
          downloadAndGenerateCsv(response.data);
        }
      },
      onError: () => {
        if (onFinally) onFinally();
      },
    });
  };

  const onAddNewDocumentInShipment = (csvDocument: ShipmentDocument) => {
    const documentsClone = _.cloneDeep(documents || []);

    documentsClone.push(csvDocument);
    onChangeDocuments?.(documentsClone);
  };

  const handleDownloadCommercialInvoicePackingList = (
    bookingId: number,
    onFinally?: () => void
  ) => {
    downloadInvoicePackingDocument(bookingId, {
      onSuccess: (response) => {
        if (onFinally) onFinally();
        if (response && response.data && response.data.content) {
          handleGeneratePdf({
            data: response.data,
            exportFile: false,
          });
        }
      },
      onError: () => {
        if (onFinally) onFinally();
      },
    });
  };

  const handleDownloadCiplCSV = (bookingId: number, onFinally?: () => void) => {
    downloadCiplCSV(bookingId, {
      onSuccess: (response) => {
        if (onFinally) onFinally();
        if (response && response.data && response.data.content) {
          handleGeneratePdf({
            data: response.data,
            exportFile: true,
          });
        }
      },
      onError: () => {
        if (onFinally) onFinally();
      },
    });
  };

  const onDownloadDocumentCsv = (
    _: number,
    documentType?: DocumentReferenceShipment,
    onFinally?: () => void
  ) => {
    if (!bookingId) return;

    switch (documentType) {
      case DocumentReferenceOptions.SHIPMENT.COMMERCIAL_INVOICE_PACKING_LIST:
        handleDownloadCiplCSV(bookingId, onFinally);
        break;
      default:
        break;
    }
  };

  const onDownloadDocument = (
    _: number,
    documentType?: DocumentReferenceShipment,
    onFinally?: () => void
  ) => {
    if (!bookingId) return;

    switch (documentType) {
      case DocumentReferenceOptions.SHIPMENT.COMMERCIAL_INVOICE_PACKING_LIST:
        handleDownloadCommercialInvoicePackingList(bookingId, onFinally);
        break;
      case DocumentReferenceOptions.SHIPMENT.WAREHOUSE_SCAN_DATA:
        handleDownloadCartonLogs(bookingId, onFinally);
        break;
      default:
        break;
    }
  };
  const { mutate: onUploadDocument, isLoading: loadingSavingCsvDocument } = useUploadDocument(
    onAddNewDocumentInShipment
  );

  const saveShipmentDocuments = () => {
    if (!bookingId) return;

    updateShipmentDocuments({ shipmentId: bookingId, documents });
  };

  const loadingAllDocuments = useMemo(
    () =>
      loadingBookingDocuments ||
      loadingCiplCsv ||
      loadingCartonLogs ||
      loadingAllDocumentsZip ||
      loadingUpdateShipmentDocuments,
    [
      loadingBookingDocuments,
      loadingCiplCsv,
      loadingCartonLogs,
      loadingAllDocumentsZip,
      loadingUpdateShipmentDocuments,
    ]
  );

  return {
    onDownloadDocument,
    onDownloadDocumentCsv,
    loadingCartonLogs,
    loadingBookingDocuments,
    loadingCiplCsv,
    loadingAllDocumentsZip,
    onDownloadAllDocuments,
    onUploadDocument,
    loadingSavingCsvDocument,
    saveShipmentDocuments,
    loadingUpdateShipmentDocuments,
    loadingAllDocuments,
  };
};
